import { useState } from "react";
import { TIME_OFF_TYPES, type TimeOffType, type DayPart } from "../lib/shared/timeOffTypes";
import { createRequest, type TimeOffRequest } from "../lib/api";

interface Props {
  onCreated: (request: TimeOffRequest) => void;
  onCancel: () => void;
}

const today = new Date().toISOString().slice(0, 10);

export default function RequestForm({ onCreated, onCancel }: Props) {
  const [type, setType] = useState<TimeOffType>("vacation");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dayPart, setDayPart] = useState<DayPart>("full");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSingleDay = startDate === endDate;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (endDate < startDate) {
      setError("End date can't be before the start date.");
      return;
    }

    setSubmitting(true);
    try {
      // Same Intl API already used for the header's "City · EDT" greeting
      // (see src/lib/greeting.ts) -- reused here so half-day Calendar events
      // land on the correct local clock hours for whoever's submitting.
      let timeZone: string | undefined;
      try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        // Intl.DateTimeFormat isn't universally supported -- the backend
        // falls back to a default timezone if this is left undefined.
      }

      const created = await createRequest({
        type,
        startDate,
        endDate,
        dayPart: isSingleDay ? dayPart : "full",
        note: note.trim() || undefined,
        timeZone,
      });
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="di-card request-form" onSubmit={handleSubmit}>
      <h2 className="di-h4">New request</h2>

      <div className="form-row">
        <label className="di-small" htmlFor="type">
          Type
        </label>
        <div className="type-toggle" role="radiogroup" aria-label="Time off type">
          {(Object.keys(TIME_OFF_TYPES) as TimeOffType[]).map((key) => (
            <button
              key={key}
              type="button"
              className={`type-chip ${type === key ? "type-chip-active" : ""}`}
              onClick={() => setType(key)}
              aria-pressed={type === key}
            >
              {TIME_OFF_TYPES[key].label}
            </button>
          ))}
        </div>
        {!TIME_OFF_TYPES[type].needsApproval && (
          <p className="di-small">Sick time is logged automatically -- no approval needed.</p>
        )}
      </div>

      <div className="form-row form-row-split">
        <div>
          <label className="di-small" htmlFor="start-date">
            Start date
          </label>
          <input
            id="start-date"
            type="date"
            className="di-input"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              if (e.target.value > endDate) setEndDate(e.target.value);
            }}
            required
          />
        </div>
        <div>
          <label className="di-small" htmlFor="end-date">
            End date
          </label>
          <input
            id="end-date"
            type="date"
            className="di-input"
            value={endDate}
            min={startDate}
            onChange={(e) => {
              const value = e.target.value;
              // Belt-and-suspenders alongside the min attribute above: some
              // browsers/OSes only enforce min on their calendar picker UI,
              // not on manually-typed values, until form submission. Clamping
              // here means the state can never hold start > end at all.
              setEndDate(value < startDate ? startDate : value);
            }}
            required
          />
        </div>
      </div>

      {isSingleDay && (
        <div className="form-row">
          <label className="di-small">Portion of day</label>
          <div className="type-toggle" role="radiogroup" aria-label="Portion of day">
            {(
              [
                ["full", "Full day"],
                ["half-am", "Morning"],
                ["half-pm", "Afternoon"],
              ] as [DayPart, string][]
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`type-chip ${dayPart === value ? "type-chip-active" : ""}`}
                onClick={() => setDayPart(value)}
                aria-pressed={dayPart === value}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="form-row">
        <label className="di-small" htmlFor="note">
          Note (optional)
        </label>
        <textarea
          id="note"
          className="di-input"
          rows={3}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything your approver should know"
        />
      </div>

      <p className="di-small form-note">
        Please make sure you are coordinating your schedule with your Project Managers for any
        client-facing work.
      </p>

      {error && <p className="form-error">{error}</p>}

      <div className="form-actions">
        <button type="button" className="di-btn di-btn-outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="di-btn di-btn-primary" disabled={submitting}>
          {submitting ? "Submitting…" : "Submit request"}
        </button>
      </div>
    </form>
  );
}
